import _ from "lodash";
import React, { useReducer } from "react";
import { useTranslation } from "react-i18next";
import i18next from "src/i18n/config";
import { makeStyles } from "tss-react/mui";

import { AddMemberDialog } from "@dashboard/shared/components/AddMemberDialog";
import { PaneElement } from "@dashboard/shared/components/SidePanel/shared/PaneElement";
import { useCurrentSubscriptionExpired, useCurrentWorkspace } from "@dashboard/workspaces/api/helpers";
import { Group, GroupMember } from "@devices/types";
import { Box, Table, TableRow, Theme, useTheme } from "@mui/material";
import { memberSubject, PermissionService } from "@services/permissionService/permissionService";
import { SecondaryButton } from "@shared/CustomButton";
import { MinimalTableHeader } from "@shared/table/MinimalTableComponents";
import { TableBody } from "@shared/table/TableBody";
import { Header } from "@shared/table/TableHeader";
import { ColumnDef, flexRender, getCoreRowModel, getSortedRowModel, useReactTable } from "@tanstack/react-table";

import { ActionCell, EmailCell, RoleCell } from "./Cells";

const useStyles = makeStyles()((theme: Theme) => ({
    headerBox: { display: "flex", justifyContent: "space-between", paddingBottom: theme.spacing(2) },
    btnBox: { display: "flex", gap: theme.spacing(1) },
    twoFactorContainer: {
        display: "flex",
        alignItems: "center",
        marginBottom: theme.spacing(1),
    },
}));

export type TableData = {
    isWorkspaceOnly: boolean;
    groupId: number;
    id: number;
    status: string;
    email: string;
    role: GroupMember["role"];
};

const columns: ColumnDef<TableData, unknown>[] = [
    {
        id: "email",
        header: i18next.t("common:label.email"),
        cell: EmailCell,
        enableSorting: true,
        accessorKey: "email",
        sortingFn: "alphanumeric",
    },
    {
        id: "role",
        header: i18next.t("common:label.role"),
        cell: RoleCell,
        enableSorting: true,
        accessorKey: "role",
        sortingFn: "alphanumeric",
    },
    {
        id: "action",
        header: "",
        cell: ActionCell,
    },
];

const MemberTab = (props: Group): React.JSX.Element => {
    const [dialogOpen, setDialogOpen] = useReducer((state) => !state, false);

    const { classes } = useStyles();
    const theme = useTheme();
    const { t } = useTranslation(["sidepanels"]);

    const isExpired = useCurrentSubscriptionExpired();
    const { data: workspace } = useCurrentWorkspace();

    const { groupAbility } = PermissionService();
    const ability = groupAbility(props, workspace);
    const mayManageMember = ability.can("manage", "Member");
    const mayInviteMember = ability.can("create", "Member");

    const allMembers = React.useMemo(() => {
        return _.unionBy(props.members, workspace?.members, "email").map((member) => {
            const isGroupMember = props.members.some((groupMember) => groupMember.email === member.email);
            return { ...member, isWorkspaceOnly: !isGroupMember, groupId: props.id };
        });
    }, [props.members, workspace?.members, props.id]);

    const table = useReactTable({
        data: allMembers,
        columns: columns,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
    });

    const getAddBtnTitle = () => {
        if (!mayManageMember) {
            return { title: t("sidepanels:groupPanel.tooltip.groupRole") };
        }
        if (isExpired) {
            return { title: t("sidepanels:groupPanel.tooltip.subscriptionExpired") };
        }
        return { title: t("sidepanels:groupPanel.tooltip.addMember") };
    };

    return (
        <PaneElement.Container>
            <Box className={classes.headerBox}>
                <PaneElement.Header>{t("sidepanels:groupPanel.membersTab.groupMemberships")}</PaneElement.Header>
                <Box className={classes.btnBox}>
                    <SecondaryButton
                        onClick={setDialogOpen}
                        tooltipProps={getAddBtnTitle()}
                        style={{ marginLeft: theme.spacing(2) }}
                        disabled={!mayInviteMember}
                    >
                        <i className="fas fa-solid fa-user-plus" />
                    </SecondaryButton>
                </Box>
            </Box>
            <PaneElement.TableLayout contentDeps={[allMembers.length]}>
                <Table stickyHeader>
                    <MinimalTableHeader>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <TableRow key={headerGroup.id}>
                                {headerGroup.headers.map((header) => (
                                    <Header key={header.id} {...header} />
                                ))}
                            </TableRow>
                        ))}
                    </MinimalTableHeader>
                    <TableBody table={table} noDataMessage={t("sidepanels:groupPanel.membersTab.noMembers")}>
                        {table.getRowModel().rows.map((row) => (
                            <TableRow key={row.id}>
                                {row.getVisibleCells().map((cell) =>
                                    flexRender(cell.column.columnDef.cell, {
                                        ...cell.getContext(),
                                        key: cell.id,
                                    }),
                                )}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </PaneElement.TableLayout>
            <AddMemberDialog
                name="group"
                open={dialogOpen}
                onClose={setDialogOpen}
                id={props.id}
                canAssignOwner={ability.can("manage", memberSubject("Owner"))}
            />
        </PaneElement.Container>
    );
};

export default MemberTab;
